import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CheckCircle } from 'lucide-react';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { CreatorGroup } from '@/hooks/useGroupedCreators';
import { Creator } from '@/hooks/useCreators';

const formatCount = (n: number) => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
};

interface CreatorGroupListViewProps {
  groups: CreatorGroup[];
  onGroupClick: (group: CreatorGroup) => void;
}

export const CreatorGroupListView: React.FC<CreatorGroupListViewProps> = ({ groups, onGroupClick }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-[60px]"></TableHead>
        <TableHead>Name</TableHead>
        <TableHead>Platforms</TableHead>
        <TableHead>Followers</TableHead>
        <TableHead>Videos</TableHead>
        <TableHead>Likes</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {groups.map((group) => (
        <TableRow key={group.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onGroupClick(group)}>
          <TableCell>
            <Avatar className="h-10 w-10">
              <SafeAvatarImage src={getProxiedImageUrl(group.avatar_url)} className="object-cover" />
              <AvatarFallback className="bg-gradient-to-br from-muted to-muted/60">{(group.display_name || group.primary_name || 'U')[0]}</AvatarFallback>
            </Avatar>
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              {group.display_name || group.primary_name}
              {group.is_verified && <CheckCircle className="h-4 w-4 text-primary" />}
            </div>
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              {group.platforms.map(p => (
                <Badge key={p} variant="outline" className="text-xs capitalize">{p}</Badge>
              ))}
            </div>
          </TableCell>
          <TableCell>{formatCount(group.total_followers)}</TableCell>
          <TableCell>{group.creators.reduce((s, c) => s + (c.video_count ?? 0), 0)}</TableCell>
          <TableCell>{formatCount(group.creators.reduce((s, c) => s + (c.like_count ?? 0), 0))}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

interface CreatorListViewProps {
  creators: Creator[];
  onCreatorClick: (creator: Creator) => void;
}

export const CreatorListView: React.FC<CreatorListViewProps> = ({ creators, onCreatorClick }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-[60px]"></TableHead>
        <TableHead>Name</TableHead>
        <TableHead>Platform</TableHead>
        <TableHead>Followers</TableHead>
        <TableHead>Videos</TableHead>
        <TableHead>Likes</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {creators.map((creator) => (
        <TableRow key={creator.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onCreatorClick(creator)}>
          <TableCell>
            <Avatar className="h-10 w-10">
              <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
              <AvatarFallback className="bg-gradient-to-br from-muted to-muted/60">{(creator.display_name || creator.username)[0]}</AvatarFallback>
            </Avatar>
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              {creator.display_name}
              {creator.is_verified && <CheckCircle className="h-4 w-4 text-primary" />}
            </div>
            <p className="text-xs text-muted-foreground">@{creator.username}</p>
          </TableCell>
          <TableCell>
            <Badge variant="outline" className="capitalize text-xs">{creator.platform}</Badge>
          </TableCell>
          <TableCell>{formatCount(creator.follower_count)}</TableCell>
          <TableCell>{creator.video_count}</TableCell>
          <TableCell>{formatCount(creator.like_count)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
